import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, CheckCircle2, PhoneCall, Target, BarChart3, Wallet, Phone, ArrowLeft, Star, HelpCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { useToast } from "../hooks/use-toast";
import Reveal from "../components/Reveal";
import AnimatedCounter from "../components/AnimatedCounter";
import Seo from "../components/Seo";
import { networkVerticals, networkProcess, networkStats, company } from "../data/mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const benefits = [
  { icon: PhoneCall, title: "Exclusive live calls", desc: "Real inbound calls from people who want your service now — never shared or recycled." },
  { icon: Target, title: "Precise targeting", desc: "Filter by geography and qualification criteria so every call fits your business." },
  { icon: BarChart3, title: "Full transparency", desc: "Real-time call tracking and reporting on every lead we deliver to you." },
  { icon: Wallet, title: "Pay per qualified call", desc: "Only pay for calls that meet your duration and quality thresholds." },
];

const VerticalLanding = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const vertical = networkVerticals.find((v) => v.slug === slug);

  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  const scrollToForm = () => {
    document.getElementById("quote-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (!vertical) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Vertical not found</h1>
        <p className="text-slate-500">This page may have moved.</p>
        <Link to="/networks"><Button className="brand-gradient text-white font-bold"><ArrowLeft className="mr-2 h-4 w-4" />Back to Networks</Button></Link>
      </div>
    );
  }

  const Icon = vertical.icon;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast({ title: "A few details needed", description: "Please add your name, email, and phone.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: "Pay Per Call Network",
        message: `Requesting a pay-per-call quote for the ${vertical.name} vertical.`,
        answers: { vertical: vertical.name, source: `Landing page: /networks/${vertical.slug}` },
      });
      navigate("/thank-you");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({ title: "Something went wrong", description: typeof detail === "string" ? detail : "Please try again or give us a call.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden">
      <Seo title={`${vertical.name} Pay-Per-Call`} description={vertical.subhead} />

      {/* HERO with quote form */}
      <section className="relative navy-gradient overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="blob h-72 w-72 bg-blue-500 -top-10 right-1/4" />
        <div className="relative mx-auto max-w-7xl px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Link to="/networks" className="inline-flex items-center gap-2 text-blue-200 hover:text-white transition-colors text-sm font-semibold">
              <ArrowLeft className="h-4 w-4" /> All verticals
            </Link>
            <div className="mt-6 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/15">
                <Icon className="h-6 w-6 text-blue-200" />
              </div>
              <span className="text-sm font-bold uppercase tracking-[0.2em] text-blue-300">{vertical.name} · Pay Per Call</span>
            </div>
            <h1 className="mt-5 font-display text-4xl md:text-5xl font-extrabold text-white leading-[1.08]">
              {vertical.headline}
            </h1>
            <p className="mt-5 text-lg text-blue-100/85 max-w-xl">{vertical.subhead}</p>
            <div className="mt-7 grid sm:grid-cols-2 gap-3">
              {vertical.bullets.map((b) => (
                <div key={b} className="flex items-center gap-2.5 text-blue-50 font-medium">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" /> {b}
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-blue-200/80">
              <span className="flex items-center gap-1.5"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> Performance-based pricing</span>
              <a href={company.phoneHref} className="flex items-center gap-1.5 hover:text-white transition-colors"><Phone className="h-4 w-4" /> {company.phone}</a>
            </div>
          </div>

          {/* Quote form */}
          <Reveal className="relative">
            <div id="quote-form" className="rounded-3xl bg-white shadow-2xl p-8 scroll-mt-24" data-testid="vertical-quote-form">
              <h2 className="font-display text-2xl font-extrabold text-slate-900">Get {vertical.name} calls</h2>
              <p className="mt-1 text-slate-600">Request a free, no-obligation quote. We'll reply within one business day.</p>
              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full name *</Label>
                  <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Smith" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="jane@business.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(760) 000-0000" />
                </div>
                <Button type="submit" size="lg" disabled={submitting} className="w-full brand-gradient text-white font-bold text-base group">
                  {submitting ? "Sending..." : "Get My Free Quote"}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
                <p className="text-center text-xs text-slate-400">Prefer to talk now? <a href={company.bookingUrl} target="_blank" rel="noreferrer" className="font-semibold text-blue-600 hover:underline">Book a call</a></p>
              </form>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">Why our pay-per-call</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Calls built to grow {vertical.name.toLowerCase()}</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <Reveal key={b.title} delay={i * 70}>
                <div className="h-full rounded-2xl border border-slate-200 p-7 card-lift bg-gradient-to-b from-white to-slate-50/50">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl brand-gradient shadow-lg">
                    <b.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="section-pad bg-slate-50">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">How it works</p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">Live calls, on performance</h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {networkProcess.map((p, i) => (
              <Reveal key={p.step} delay={i * 80}>
                <div className="relative h-full rounded-2xl bg-white border border-slate-200 p-7 card-lift">
                  <span className="font-display text-5xl font-extrabold text-slate-100 absolute top-4 right-5 select-none">{p.step}</span>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                    <p.icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative section-pad navy-gradient overflow-hidden">
        <div className="blob h-72 w-72 bg-blue-500 top-0 left-1/4" />
        <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {networkStats.map((s, i) => (
            <Reveal key={i} delay={i * 80} className="text-center">
              <AnimatedCounter end={s.value} suffix={s.suffix} className="font-display text-5xl md:text-6xl font-extrabold text-white" />
              <p className="mt-3 text-sm font-semibold uppercase tracking-wide text-blue-200/80">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      {vertical.faqs?.length > 0 && (
        <section className="section-pad bg-slate-50" data-testid="vertical-faq-section">
          <div className="mx-auto max-w-3xl px-6">
            <Reveal className="text-center">
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-600">FAQ</p>
              <h2 className="mt-3 font-display text-3xl md:text-4xl font-extrabold text-slate-900">
                {vertical.name} pay-per-call questions
              </h2>
              <p className="mt-4 text-slate-600">Everything you need to know before we launch your campaign.</p>
            </Reveal>
            <Reveal delay={80} className="mt-10">
              <Accordion type="single" collapsible className="rounded-2xl bg-white border border-slate-200 px-6 shadow-sm">
                {vertical.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="border-slate-100 last:border-0" data-testid={`vertical-faq-item-${i}`}>
                    <AccordionTrigger className="py-5 text-base font-bold text-slate-900 hover:no-underline">
                      <span className="flex items-center gap-3 text-left">
                        <HelpCircle className="h-5 w-5 shrink-0 text-blue-600" />
                        {f.q}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 pl-8 text-[15px] leading-relaxed text-slate-600">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
            <Reveal delay={120} className="mt-8 text-center">
              <p className="text-slate-600">Still have questions?</p>
              <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={scrollToForm} className="brand-gradient text-white font-bold" data-testid="faq-get-quote-btn">
                  Get a Free Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <a href={company.phoneHref}>
                  <Button variant="outline" className="font-bold border-slate-300 hover:border-blue-600 hover:text-blue-600 w-full">
                    <Phone className="mr-2 h-4 w-4" /> {company.phone}
                  </Button>
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="section-pad bg-white">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <div className="rounded-3xl bg-slate-50 border border-slate-200 p-10 md:p-16 text-center">
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-slate-900">Ready for more {vertical.name.toLowerCase()} calls?</h2>
              <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">Let's build a pay-per-call campaign tailored to your business and service area.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <a href={company.bookingUrl} target="_blank" rel="noreferrer">
                  <Button size="lg" className="brand-gradient text-white font-bold group w-full sm:w-auto">
                    Schedule a Call
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </a>
                <a href={company.phoneHref}>
                  <Button size="lg" variant="outline" className="font-bold border-slate-300 hover:border-blue-600 hover:text-blue-600 w-full sm:w-auto">
                    <Phone className="mr-2 h-5 w-5" /> {company.phone}
                  </Button>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Spacer so sticky mobile CTA doesn't cover content */}
      <div className="lg:hidden h-20" aria-hidden="true" />

      {/* Sticky mobile Get a Quote button */}
      <div className="lg:hidden fixed inset-x-0 bottom-0 z-50 p-3 bg-gradient-to-t from-white via-white/95 to-transparent">
        <Button
          onClick={scrollToForm}
          size="lg"
          className="w-full brand-gradient text-white font-bold text-base shadow-2xl group"
          data-testid="sticky-quote-btn"
        >
          Get a Free Quote
          <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-0.5" />
        </Button>
      </div>
    </div>
  );
};

export default VerticalLanding;
